<template>
  <div class="official-seller-wrapper" ref="wrapper">
    <div
      class="official-seller-badge"
      @click.stop="togglePopup"
      @mouseenter="openPopup"
      @mouseleave="scheduleClose"
      role="button"
      :aria-label="$t('official_seller_more_info')"
      aria-haspopup="true"
      :aria-expanded="isOpen.toString()"
      tabindex="0"
      @keydown.enter.stop="togglePopup"
      @keydown.space.prevent.stop="togglePopup"
    >
      <!-- Shield + check icon -->
      <svg class="shield-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
        <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
        <polyline points="9 12 11 14 15 10"/>
      </svg>
      <span class="badge-text">{{ $t('official_seller') }}</span>
      <!-- Info circle icon (visual only) -->
      <svg class="info-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
        <circle cx="12" cy="12" r="10"/>
        <line x1="12" y1="16" x2="12" y2="12"/>
        <line x1="12" y1="8" x2="12.01" y2="8"/>
      </svg>
    </div>

    <transition name="popup-fade">
      <div
        v-if="isOpen"
        class="official-seller-popup"
        role="tooltip"
        @mouseenter="cancelClose"
        @mouseleave="scheduleClose"
      >
        <div class="popup-header">
          <svg class="popup-shield" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
            <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
            <polyline points="9 12 11 14 15 10"/>
          </svg>
          <span class="popup-title">{{ $t('official_seller') }}</span>
        </div>
        <p class="popup-desc">{{ $t('official_seller_desc') }}</p>
      </div>
    </transition>
  </div>
</template>

<script>
export default {
  name: 'OfficialSellerBadge',
  data() {
    return {
      isOpen: false,
      closeTimer: null,
    };
  },
  methods: {
    openPopup() {
      if (this.closeTimer) {
        clearTimeout(this.closeTimer);
        this.closeTimer = null;
      }
      this.isOpen = true;
    },
    scheduleClose() {
      this.closeTimer = setTimeout(() => {
        this.isOpen = false;
      }, 200);
    },
    cancelClose() {
      if (this.closeTimer) {
        clearTimeout(this.closeTimer);
        this.closeTimer = null;
      }
    },
    togglePopup() {
      if (this.isOpen) {
        this.isOpen = false;
      } else {
        this.openPopup();
      }
    },
    handleOutsideClick(e) {
      if (this.$refs.wrapper && !this.$refs.wrapper.contains(e.target)) {
        this.isOpen = false;
      }
    },
  },
  mounted() {
    document.addEventListener('click', this.handleOutsideClick);
  },
  beforeUnmount() {
    document.removeEventListener('click', this.handleOutsideClick);
    if (this.closeTimer) clearTimeout(this.closeTimer);
  },
};
</script>

<style scoped>
.official-seller-wrapper {
  position: relative;
  display: inline-block;
}

.official-seller-badge {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: rgba(255, 255, 255, 0.93);
  border: 1.5px solid #E4DAFF;
  border-radius: 100px;
  padding: 5px 12px 5px 10px;
  box-shadow: 0 2px 10px rgba(95, 46, 234, 0.12);
  backdrop-filter: blur(6px);
  -webkit-backdrop-filter: blur(6px);
  cursor: pointer;
  user-select: none;
  -webkit-user-select: none;
  transition: box-shadow 0.15s, background 0.15s;
}

.official-seller-badge:hover {
  box-shadow: 0 4px 16px rgba(95, 46, 234, 0.2);
  background: rgba(255, 255, 255, 1);
}

.shield-icon {
  width: 15px;
  height: 15px;
  color: #5F2EEA;
  flex-shrink: 0;
}

.badge-text {
  font-size: 12px;
  font-weight: 700;
  color: #5F2EEA;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  white-space: nowrap;
}

.info-icon {
  width: 14px;
  height: 14px;
  color: #5F2EEA;
  opacity: 0.6;
  flex-shrink: 0;
  margin-left: 2px;
  transition: opacity 0.15s;
}

.official-seller-badge:hover .info-icon {
  opacity: 1;
}

/* Popup */
.official-seller-popup {
  position: absolute;
  top: calc(100% + 10px);
  left: 50%;
  transform: translateX(-50%);
  width: 270px;
  background: #ffffff;
  border-radius: 12px;
  box-shadow: 0 8px 32px rgba(95, 46, 234, 0.16), 0 2px 8px rgba(0, 0, 0, 0.08);
  border: 1.5px solid #E4DAFF;
  padding: 14px 16px;
  z-index: 9999;
  text-align: left;
}

/* Arrow above popup */
.official-seller-popup::before {
  content: '';
  position: absolute;
  bottom: 100%;
  left: 50%;
  transform: translateX(-50%);
  border: 7px solid transparent;
  border-bottom-color: #E4DAFF;
}

.official-seller-popup::after {
  content: '';
  position: absolute;
  bottom: calc(100% - 1.5px);
  left: 50%;
  transform: translateX(-50%);
  border: 7px solid transparent;
  border-bottom-color: #ffffff;
  z-index: 1;
}

.popup-header {
  display: flex;
  align-items: center;
  gap: 7px;
  margin-bottom: 8px;
}

.popup-shield {
  width: 17px;
  height: 17px;
  color: #5F2EEA;
  flex-shrink: 0;
}

.popup-title {
  font-size: 12px;
  font-weight: 700;
  color: #5F2EEA;
  text-transform: uppercase;
  letter-spacing: 0.06em;
}

.popup-desc {
  font-size: 13px;
  color: #4B5563;
  line-height: 1.55;
  margin: 0;
}

/* Transition */
.popup-fade-enter-active,
.popup-fade-leave-active {
  transition: opacity 0.15s ease, transform 0.15s ease;
}

.popup-fade-enter-from,
.popup-fade-leave-to {
  opacity: 0;
  transform: translateX(-50%) translateY(-4px);
}
</style>
